import { motion } from 'motion/react'
import { Calendar, Check, Mail } from 'lucide-react'
import { BookingWidget } from '@/components/BookingWidget'
import type { IntakeFormData, ServiceId, BudgetRange, SubmissionResult } from '@/hooks/useIntakeWizard'

interface ScheduleStepProps {
  formData: IntakeFormData
  result: SubmissionResult | null
}

const SERVICE_LABELS: Record<ServiceId, string> = {
  'cms-platform': 'CMS platform ($25/mo base)',
  'new-website-content-ready': 'Website build (content ready)',
  'new-website-with-coaching': 'Website build + content coaching',
  'branding-logo': 'Branding / logo design',
  'custom-design': 'Custom design / brand polish',
  'ongoing-updates': 'Updates managed by Garrett',
  'email-marketing': 'Email marketing',
  'social-media': 'Social media content',
}

const BUDGET_LABELS: Record<BudgetRange, string> = {
  '20-50': '$20 – $50/mo',
  '50-150': '$50 – $150/mo',
  '150-300': '$150 – $300/mo',
  '300-600': '$300 – $600/mo',
  '600-plus': '$600+/mo',
  unsure: 'Not sure yet',
}

export function ScheduleStep({ formData, result }: ScheduleStepProps) {
  const firstName = formData.name.split(' ')[0]
  void result

  const selectedServices = formData.services.map((s) => SERVICE_LABELS[s]).filter(Boolean)
  const budgetLabel = formData.budgetRange ? BUDGET_LABELS[formData.budgetRange] : null

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100"
        >
          <Check className="h-7 w-7 text-green-600" strokeWidth={3} />
        </motion.div>
        <h2 className="text-foreground text-3xl font-bold md:text-4xl">Thanks, {firstName}! Pick a time.</h2>
        <p className="text-muted-foreground text-lg">
          Your info is saved. Grab a 30-minute slot below and we'll walk through what you need.
        </p>
      </motion.div>

      {/* Summary card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-muted/30 space-y-3 rounded-lg p-5 text-sm"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Organization</div>
            <div className="mt-0.5 font-medium">{formData.organization}</div>
          </div>
          {budgetLabel && (
            <div>
              <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Monthly budget</div>
              <div className="mt-0.5 font-medium">{budgetLabel}</div>
            </div>
          )}
        </div>
        {selectedServices.length > 0 && (
          <div>
            <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              Services of interest
            </div>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {selectedServices.map((label) => (
                <li
                  key={label}
                  className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
                >
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </motion.div>

      {/* Embedded Calendly */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="border-border overflow-hidden rounded-lg border"
      >
        <div className="bg-muted/40 border-border flex items-center gap-2 border-b px-4 py-2 text-sm font-medium">
          <Calendar className="text-primary h-4 w-4" />
          Book a 30-minute discovery call
        </div>
        <div className="p-4">
          <BookingWidget
            prefill={{ name: formData.name, email: formData.email }}
            metadata={{ source_page: 'intake-wizard' }}
            className="border-0 p-0 shadow-none"
          />
        </div>
      </motion.div>

      {/* Email escape hatch */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-center">
        <p className="text-muted-foreground inline-flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4" />
          Prefer email? Reply to the confirmation we just sent — no need to book.
        </p>
      </motion.div>
    </div>
  )
}
